import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import InfiniteScroll from "react-infinite-scroller";
import Loader from "components/Loader/Loader";
import MDPSettingsItem from "./MDPSettingsItem";
import { ItemsWrapper } from "./mDPSettingsBlockStyles";
import { NotFoundDiv } from "../CommonSettingsBlock/CommonSettingsBlockStyles";
import {
    setMDPSelectedItem,
    TickerConfig,
    addMDPRow,
    setMDPOpenState,
    deleteMDPRowAndSave,
    ConfigFileDetails,
} from "store/reducers/SettingsSlice";
import { useAppSelector } from "hooks/redux";
import AreYouSureModal from "components/AreYouSureModal/AreYouSureModal";

interface MDPListProps {
    config: TickerConfig;
    setSelectedRowIndex: React.Dispatch<React.SetStateAction<number | null>>;
    params: string[];
    updateField: (rowIndex: number, paramIdx: number, value: string) => void;
    rows: { row: string[]; originalIndex: number }[];
    isSavingError: boolean;
    selectedRowIndex?: number | null;
    configMetadata?: ConfigFileDetails;
}

const MDPList = ({
    config,
    setSelectedRowIndex,
    params,
    updateField,
    rows,
    isSavingError,
    selectedRowIndex,
    configMetadata,
}: MDPListProps) => {
    const { id: configId } = config;
    const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(
        null,
    );
    const dispatch = useDispatch();
    const {
        MDPTypes,
        MDPParamTypes,
        activeTicker,
        activeGroup,
        mdpOpenStates,
        error,
    } = useAppSelector((s) => s.settings);
    const openRowsForThisConfig = useMemo(() => {
        const record = mdpOpenStates?.find(
            (item) => item.config_id === config.id,
        );
        return record?.open_rows?.map(Number) || [];
    }, [mdpOpenStates, config.id]);

    const isUseIdx = params.indexOf("isUse");
    const items = useMemo(() => {

        return rows.map(({ row, originalIndex }) => ({
            SheduleID: row[0] || "",
            idSource: row[2] || "",
            sourceType: row[3] || "",
            all_values: row,

            SourceTypeSheduleID: row[4],
            isActive: true,
            isCopy: (row[2] || "").includes("::copy"),
            rowId: `row_${originalIndex}`,
            originalIndex,
        }));
    }, [rows]);

    const selectedSCId = useAppSelector((store) => store.settings.selectedSCId);
    const selectedSTSId = useAppSelector(
        (store) =>
            store.settings.selectedSTSId.find((item) => item.id === config.id)
                ?.value || null,
    );

    const toggleActive = (originalIndex: number, checked: boolean) => {
        if (isUseIdx === -1) return;
        updateField(originalIndex, isUseIdx, checked ? "1" : "0");
    };

    const handleRowSelect = (originalIndex: number) => {
        const isAlreadyOpen = selectedRowIndex === originalIndex;
        if (isAlreadyOpen) {
            dispatch(
                setMDPOpenState({
                    group_name: activeGroup.name,
                    ticker_name: activeTicker.name,
                    config_id: config.id,
                    open_row: "",
                    schedule_id: activeTicker.schedule_id || undefined,
                }),
            );

            setSelectedRowIndex(null);

            return;
        }

        const isOpenInAnotherConfig = mdpOpenStates?.some(
            (item) =>
                item.config_id !== config.id &&
                item.open_rows?.includes(String(originalIndex + 1)),
        );

        if (isOpenInAnotherConfig) {
            return;
        }

        dispatch(
            setMDPOpenState({
                group_name: activeGroup.name,
                ticker_name: activeTicker.name,
                config_id: config.id,
                open_row: String(originalIndex + 1),
                schedule_id: activeTicker.schedule_id || undefined,
            }),
        );

        setSelectedRowIndex(originalIndex);
    };

    const duplicate = (originalIndex: number) => {
        const sourceRow = rows.find((r) => r.originalIndex === originalIndex);
        if (!sourceRow) return;

        const copyRow = [...sourceRow.row];

        if (copyRow[copyRow.length - 1] === "new_temp_flag") {
            copyRow.pop();
        }
        dispatch(addMDPRow({ configId, newRow: copyRow }));
    };

    const handleDeleteClick = (originalIndex: number) => {
        setDeleteConfirmIndex(originalIndex);
    };

    const handleDeleteRow = (originalIndex: number) => {
        if (deleteConfirmIndex === null) return;

        if (openRowsForThisConfig.includes(deleteConfirmIndex + 1)) {
            dispatch(
                setMDPOpenState({
                    group_name: activeGroup.name,
                    ticker_name: activeTicker.name,
                    config_id: config.id,
                    open_row: "",
                    schedule_id: activeTicker.schedule_id || undefined,
                }),
            );
            setSelectedRowIndex(null);
        }

        dispatch(
            deleteMDPRowAndSave({ configId, rowIndex: deleteConfirmIndex }),
        );

        setDeleteConfirmIndex(null);
    };
    if (activeTicker.name === "Название тикера") return null;

    return (
        <>
            <ItemsWrapper>
                {error && <NotFoundDiv>{error.toString()}</NotFoundDiv>}
                {items.length === 0 && (
                    <NotFoundDiv>MDP data not found</NotFoundDiv>
                )}
                <InfiniteScroll
                    pageStart={0}
                    loadMore={() => {}}
                    hasMore={false}
                    loader={<Loader size="large" key="loader" />}
                    useWindow={false}
                >
                    {items.map((item) => {
                        const isThisRowBlocked = mdpOpenStates?.some(
                            (record) =>
                                record.config_id !== config.id &&
                                record.open_rows?.includes(
                                    String(item.originalIndex + 1),
                                ),
                        );

                        const isAlreadyOpenHere =
                            selectedRowIndex === item.originalIndex;

                        const effectiveCanSelect =
                            !isThisRowBlocked || isAlreadyOpenHere;

                        if (selectedSCId || selectedSTSId) {
                            const scMatch =
                                !selectedSCId ||
                                item.all_values[0] === selectedSCId;
                            const stsMatch =
                                !selectedSTSId ||
                                item.all_values[4] === selectedSTSId;

                            if (!scMatch || !stsMatch) return null;
                        }

                        return (
                            <MDPSettingsItem
                                configId={config.id}
                                key={`key=${item.originalIndex}-${item.rowId}`}
                                openRowsForThisConfig={openRowsForThisConfig}
                                isSavingError={isSavingError}
                                MDPParamTypes={MDPParamTypes}
                                is_horizontal={config.is_horizontal}
                                isRowBlocked={isThisRowBlocked}
                                configMetadata={configMetadata}
                                isOpen={isAlreadyOpenHere}
                                onRowSelect={
                                    effectiveCanSelect
                                        ? () =>
                                              handleRowSelect(
                                                  item.originalIndex,
                                              )
                                        : undefined
                                }
                                activeTickerName={activeTicker.name}
                                item={item}
                                params={params}
                                MDPTypes={MDPTypes}
                                onToggleActive={(checked) =>
                                    toggleActive(item.originalIndex, checked)
                                }
                                onDuplicate={() =>
                                    duplicate(item.originalIndex)
                                }
                                onDelete={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(item.originalIndex);
                                }}
                                onFieldChange={(paramIdx, value) =>
                                    updateField(
                                        item.originalIndex,
                                        paramIdx,
                                        value,
                                    )
                                }
                                onSettingClick={() =>
                                    dispatch(
                                        setMDPSelectedItem({
                                            SPname: item.idSource.replace(
                                                /::copy.*$/,
                                                "",
                                            ),
                                        }),
                                    )
                                }
                                selectedRowIndex={selectedRowIndex}
                            />
                        );
                    })}
                </InfiniteScroll>
            </ItemsWrapper>
            <AreYouSureModal
                isOpen={deleteConfirmIndex !== null}
                title="Удалить строку?"
                description="Это действие нельзя отменить"
                onRequestClose={() => setDeleteConfirmIndex(null)}
                onApplyClick={handleDeleteRow}
            />
        </>
    );
};

export default MDPList;
